import { breakableSpaces } from "../../../model/html-text";
import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
} from "@angular/core";
import { BehaviorSubject, Observer } from "rxjs";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { RecurrenceService } from "../../../services/recurrence.service";
import {
    RecurrenceChange,
    RecurrenceFuture,
    RecurrencePattern,
    RecurrenceView,
} from "../../../model/recurrence";
import { TicketService } from "../../../services/ticket.service";
import { AuthService } from "../../../auth.service";
import { NavigationEnd, Router } from "@angular/router";
import { Location } from "@angular/common";
import { Messages } from "../../../model/messages";
import { ActionButtonSubmit } from "../../../model/action-button-submit";
import { Ticket } from "../../../model/ticket";
import { NavigationHelper } from "../../../services/navigation.helper";
import { Navigation } from "../../../model/navigation";
import { SingleChoice } from "../../../model/singlechoice";
import { BranchService } from "../../../services/branch.service";

import { StatusService } from "../../../services/status.service";
import { PrioritiesService } from "../../../services/priorities.service";
import { DepartmentsService } from "../../../services/departaments.service";
import { UsersService } from "../../../services/users.service";
import { DependentChoice } from "../../../model/dependentchoice";
import { DomSanitizer } from "@angular/platform-browser";

import { Comment } from "../../../model/comments";
import { Task } from "../../../model/task";
import { Involved } from "../../../model/involved";
import { Media } from "../../../model/media";
import { Labeler } from "../../../model/labeler";
import { Priority } from "../../../model/priority";
import { MatTabChangeEvent } from "@angular/material/tabs";
import { animate, style, transition, trigger } from "@angular/animations";
import { CommentsComponent } from "../../comments/comments.component";
import { MediaListComponent } from "../../media-list/media-list.component";
import { TimeLine } from "../../../model/time-line";
import { CommentsService } from "../../../services/comments.service";
import { InvolvedService } from "../../../services/involved.service";
import { EnvironmentHelper } from "../../../services/env.helper";
import { InvolvedComponent } from "../../involved/involved/involved.component";

/**
 * The fields of a ticket that stay as the series made them until its day comes,
 * as the server last handed them out.
 */
interface LockedUntilActive {
    ticketId: number;
    scheduledOn: string;
    title: string;
    description: string;
    statusId: number;
    priorityId: number;
    branchId: number;
    departmentId: number;
    archived: boolean;
}

@Component({
    selector: "ticket-details",
    templateUrl: "./ticket-details.component.html",
    styleUrls: ["./ticket-details.component.css"],
    animations: [
        trigger("tabSlide", [
            transition("void => next", [
                style({ transform: "translateX(100%)" }),
                animate("300ms ease-out", style({ transform: "translateX(0%)" })),
            ]),
            transition("void => prev", [
                style({ transform: "translateX(-100%)" }),
                animate("300ms ease-out", style({ transform: "translateX(0%)" })),
            ]),
            transition("* => void", [
                animate("300ms ease-in", style({ transform: "translateX(-100%)" })),
            ]),
        ]),
    ],
})
export class TicketDetailsComponent implements OnInit {
    @Output() choiceEvent: EventEmitter<SingleChoice> = new EventEmitter();
    @Output() dependentEvent: EventEmitter<DependentChoice> = new EventEmitter();
    messages = new BehaviorSubject<Messages>(Messages.EMPTY);
    ticket = new BehaviorSubject<Ticket>(Ticket.EMPTY);
    branchLabeler = new BehaviorSubject<string>("-");
    statusLabeler = new BehaviorSubject<string>("-");
    priorityLabeler = new BehaviorSubject<string>("-");
    departmentLabeler = new BehaviorSubject<string>("-");
    assignedLabeler = new BehaviorSubject<string>("-");
    completionUrl = new BehaviorSubject<string>("");
    ticketForm: FormGroup;
    ticketTitle: string = "ticketDetails.title.exist.ticket";

    /**
     * Het terugkeerpatroon van dit ticket, of null voor een gewoon ticket.
     *
     * Let op: dit veld is alleen betrouwbaar in leesmodus. readRecurrence vult
     * het opnieuw bij elke emissie van selectedTicketObservable, en tijdens het
     * bewerken komt daar een ticket voorbij zonder parts - dan staat het weer op
     * null terwijl het ticket wel degelijk een reeks heeft. Het scherm toont het
     * patroon dan nog steeds, want dat komt uit ticketForm, niet hieruit.
     *
     * Baseer er dus geen beslissing op tijdens het opslaan. Twee keer is dat
     * misgegaan: het formulier wiste het net ingevulde patroon, en het opslaan
     * dacht dat een bestaande reeks een nieuwe was en sloeg de vraag aan de
     * gebruiker over. Neem wat in ticketForm staat, of vraag het de server.
     */
    recurrence: RecurrenceView | null = null;

    /**
     * For a ticket the series has planned for a later day: that day, and what
     * the stored ticket said. Null for every other ticket.
     *
     * Kept apart from `recurrence` because that field empties itself while
     * editing, and this one has to hold exactly then: the save decides on it.
     * It is taken from a ticket that came with its parts, and a ticket passing
     * by without them leaves it alone.
     */
    private lockedUntilActive: LockedUntilActive | null = null;

    isLoading: boolean = false;

    commentsVisible: boolean = false;
    comments = new BehaviorSubject<Comment[]>([]);
    timelineVisible: boolean = false;

    involvedVisible: boolean = false;
    involveds = new BehaviorSubject<Involved[]>([]);
    mediaVisible: boolean = false;
    media = new BehaviorSubject<Media[]>([]);
    tasks = new BehaviorSubject<Task>(Task.EMPTY);
    ticketBackup: Ticket | null = null;
    stateGuard: boolean;
    /** The url whose panel answer has already been acted on. */
    private lastResumedUrl = "";
    /** The answer from the change panel, waiting for the save that follows. */
    private pendingChange: RecurrenceChange | null = null;
    disableEditingDuringChoice: boolean = false;
    createMode: boolean = true;
    editMode: boolean = false;
    isCreating: boolean = true;
    selectedIndex = 0;
    previousIndex = 0;
    @ViewChild(CommentsComponent) commentsComponent: CommentsComponent;
    @ViewChild(MediaListComponent) mediaComponent: MediaListComponent;
    @ViewChild(InvolvedComponent) involvedComponent: InvolvedComponent;
    @Input() items: any[] = [];
    firstLoad = true;
    /*
     * The topic is the tab's name in the url, and what onTabChange goes by.
     * It went by the label, which stopped matching once the labels were put
     * into Dutch: the tab changed without the url, and the next time the url
     * was read again - opening the user list for Betrokkenen does - the old
     * tab came back (#179).
     */
    tabs = [
        { label: "Opmerking", topic: "comment" },
        { label: "Media", topic: "media" },
        { label: "Betrokkenen", topic: "involved" },
        { label: "Timeline", topic: "timeline" },
    ];

    showCopied = false;
    public timelineView = new BehaviorSubject<TimeLine[]>([]);
    appBaseUrl: string;
    constructor(
        private ticketService: TicketService,
        private auth: AuthService,
        private fb: FormBuilder,
        private router: Router,
        private nav: NavigationHelper,
        private location: Location,
        private branches: BranchService,
        private status: StatusService,
        private priority: PrioritiesService,
        private department: DepartmentsService,
        private assigned: UsersService,
        private sanitizer: DomSanitizer,
        private commentsService: CommentsService,
        private involvedService: InvolvedService,
        private recurrenceService: RecurrenceService,
        private cdRef: ChangeDetectorRef,
        private env: EnvironmentHelper
    ) {
        this.appBaseUrl = this.env.appBaseUrl;
    }

    ngOnInit(): void {
        this.initializeForm();

        // The switch resolves both of its priorities out of this list, so the
        // form needs it in hand. Nothing else fetches it here: the labels in
        // the view are asked for one id at a time, and the form has no id to
        // ask about until the list says which one it means.
        if (this.priority.prioritiesData.length === 0) {
            this.priority.priorities(this.auth.token).subscribe();
        }

        this.updateEditModeFromUrl(this.router.url);

        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.updateEditModeFromUrl(this.router.url);
                this.editOrNot();
                // Coming back from a panel is a navigation, not a rebuild: on a
                // wide screen this component stays alive beside the panel, so
                // ngOnInit does not run again and the answer would go unread.
                this.resumeAfterPanel();
                this.endChoiceLeftUnanswered();
            }
        });

        const savedTabIndex = localStorage.getItem("ticketDetailsSelectedTab");
        if (savedTabIndex !== null) {
            this.selectedIndex = +savedTabIndex;
            this.previousIndex = 0;
            this.onTabChange(this.selectedIndex);
        }

        const restoredState = this.ticketService.restoreFormState();

        this.ticketForm.valueChanges.subscribe((formValue) => {
            if (this.stateGuard) {
            } else if (this.disableEditingDuringChoice) {
            } else if (this.editMode) {
                this.stateGuard = true;
                this.ticketService.setFormState(formValue);
                this.stateGuard = false;
            }
        });

        this.ticketService.formState.subscribe((restoredState) => {
            if (this.stateGuard) {
            } else if (this.editMode) {
                this.restoreFormValues(restoredState);
                this.editOrNot();
            }
        });

        this.ticket.subscribe(() => {
            this.branchLabeler.next(this.branchLabel);
            this.statusLabeler.next(this.statusLabel);
            this.priorityLabeler.next(this.priorityLabel);
            this.departmentLabeler.next(this.departmentLabel);
            this.assignedLabeler.next(this.assignedLabel);
        });

        this.ticketService.selectedTicketObservable.subscribe((selectedTicket) => {
            if (selectedTicket?.id != null) {
                this.isCreating = selectedTicket.id === -9;

                if (!this.disableEditingDuringChoice) {
                    this.ticket.next(Ticket.EMPTY);

                    this.ticket.next(selectedTicket);
                    this.readRecurrence(selectedTicket);
                    this.populateForm(selectedTicket);
                    this.updateTimelineView(selectedTicket);

                    const fullTicket = this.ticketService.currentTicket;

                    this.comments.next(fullTicket.parts?.comments ?? []);
                    this.involveds.next(fullTicket.parts?.ticketInvolved ?? []);
                    this.media.next(fullTicket.parts?.media ?? []);
                }
            }
        });

        if (!this.ticketService.currentTicket?.id) {
            this.ticketService.selectFromRouter();
        }

        this.nav.fragmentTopics.subscribe((topic) => {
            if (topic === "comment") {
                this.selectTab(0);
                this.showComments();
            } else if (topic === "involved") {
                this.selectTab(2);
                this.showInvolved();
            } else if (topic === "media") {
                this.selectTab(1);
                this.showMedia();
            } else if (topic === "timeline") {
                this.selectTab(3);
                this.showTimeline();
            }
        });

        localStorage.removeItem("ticketDetailsSelectedTab");

        this.resumeAfterPanel();
    }

    /**
     * Picks up the answer a panel left in the url.
     *
     * Ending a series can be done here and now. A changed rule cannot: the
     * ticket has not been saved yet, because the question is asked before
     * saving so that nothing is half stored if the user walks away. So the save
     * is run in full, with the answer in hand.
     */
    private resumeAfterPanel(): void {
        const url = this.router.url;
        if (url === this.lastResumedUrl) {
            return;
        }
        this.lastResumedUrl = url;

        const ticketId = Number(this.nav.freshNavigation.id);
        if (!ticketId || ticketId < 1) {
            return;
        }
        const future = this.answerFromUrl("future") as RecurrenceFuture | null;
        const onChange = this.answerFromUrl("onChange") as RecurrenceChange | null;

        if (!future && !onChange) {
            return;
        }
        // The answer has been read, so it comes out of the url again. Left in
        // place it would be a bookmark or a refresh away from running the same
        // thing a second time, and both of these throw tickets away. The save
        // still needs it, so it is held here for the rest of this round.
        this.pendingChange = onChange;
        this.forgetTheAnswer();

        if (future) {
            this.cancelRecurrence(this.auth.token, ticketId, future);
            return;
        }
        this.saveAfterAnswer();
    }

    /**
     * Takes the panel's answer back out of the address bar.
     *
     * Location rather than the router: this rewrites the address without
     * navigating, so nothing rebuilds and no one else has to know about it.
     */
    private forgetTheAnswer(): void {
        const clean = this.router.url.split("?")[0];
        this.location.replaceState(clean);
        this.lastResumedUrl = clean;
    }

    /** Runs the save that was postponed until the user had answered. */
    private saveAfterAnswer(): void {
        const action = new ActionButtonSubmit("save");
        action.setup().subscribe(() => { });
        this.saveTicket(action);
    }

    private updateEditModeFromUrl(url: string): void {
        if (url.includes("/edit")) {
            this.editMode = true;
            this.ticketTitle = "ticketDetails.title.exist.ticket";
        } else if (/^\/tickets\/\d+(\/)?$/.test(url)) {
            this.editMode = false;
            this.ticketTitle = "ticketDetails.title.exist.ticket";
        }
    }

    restoreFormValues(restoredState: Partial<Ticket>) {
        if (!this.disableEditingDuringChoice) {
            if (Object.keys(restoredState).length > 0) {
                this.populateForm(restoredState as Ticket);
            }
        }
    }

    copyOnly(event: MouseEvent, link: string) {
        event.preventDefault();
        const fullUrl = link.startsWith('http') ? link : `${window.location.origin}/${link.replace(/^\//, '')}`;

        if (navigator.clipboard) {
            navigator.clipboard.writeText(fullUrl);
        } else {
            const ta = document.createElement('textarea');
            ta.value = fullUrl;
            ta.style.position = 'fixed';
            ta.style.opacity = '0';
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
        }

        this.showCopied = true;
        setTimeout(() => { this.showCopied = false; }, 2000);
    }

    sanitizeHtml(html: string): string {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html;
        return breakableSpaces(tempDiv.textContent || tempDiv.innerText || "");
    }

    hasValue(value: string | null | undefined): boolean {
        if (!value) {
            return false;
        }

        const text = value.replace(/<[^>]*>/g, "").trim();

        return text !== "" &&
            text !== "-" &&
            text !== "--";
    }

    getDirection(i: number): string {
        if (this.selectedIndex === i) {
            return this.selectedIndex > this.previousIndex ? "next" : "prev";
        }
        return "void";
    }
    selectTab(index: number) {
        if (index !== this.selectedIndex) {
            this.previousIndex = this.selectedIndex;
            this.selectedIndex = index;
        }
    }

    onTabChange(index: number) {
        this.selectedIndex = index;
        localStorage.setItem("ticketDetailsSelectedTab", index.toString());
        this.selectTab(index);

        // A tab's component only exists while its tab is open. Render it now,
        // so it is there to be asked: from a timeout it sometimes was not yet,
        // the url then stayed on the previous tab, and the next read of the
        // url brought that tab back (#179).
        this.cdRef.detectChanges();

        switch (this.tabs[index].topic) {
            case "comment":
                this.commentsComponent?.createComment();
                this.showComments();
                break;
            case "involved":
                this.involvedComponent?.createInvolved();
                this.showInvolved();
                break;
            case "media":
                this.mediaComponent?.createMedia();
                this.showMedia();
                break;
            case "timeline":
                this.router.navigate([`tickets/${this.ticket.value.id}/fragment/timeline`]);
                this.showTimeline();
                break;
        }
    }

    showTimeline() {
        this.timelineVisible = true;
        this.commentsVisible = false;
        this.involvedVisible = false;
        this.mediaVisible = false;

        setTimeout(() => {
            const timelineSection = document.getElementById("timeline-section");
            timelineSection?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
    }

    toggleTimeline() {
        this.timelineVisible = !this.timelineVisible;
        if (this.timelineVisible) {
            const ticketId = this.ticketId;
            this.router.navigate([`tickets/${ticketId}/fragment/timeline`]);
            this.commentsVisible = false;
            this.involvedVisible = false;
            this.mediaVisible = false;
            this.showTimeline();
        }
    }

    toggleComments() {
        if (!this.commentsVisible) {
            this.commentsVisible = true;
            this.createMode = true;
            const ticketId = this.ticketId;

            this.router.navigate([`tickets/${ticketId}/fragmentedit`], {
                fragment: "comment",
            });
            this.nav.freshNavigation;

            this.commentsComponent?.createComment();

            this.showComments();
        }
    }

    showComments() {
        this.commentsVisible = true;
        this.timelineVisible = false;
        this.involvedVisible = false;
        this.mediaVisible = false;

        setTimeout(() => {
            const commentsSection = document.getElementById("comments-section");

            commentsSection?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
    }

    toggleInvolved() {
        this.involvedVisible = !this.involvedVisible;
        if (this.involvedVisible) {
            const ticketId = this.ticketId;
            this.router.navigate([`tickets/${ticketId}/fragment/involved`]);
            this.timelineVisible = false;
            this.commentsVisible = false;
            this.mediaVisible = false;
            this.showInvolved();
        }
    }

    showInvolved() {
        this.involvedVisible = true;
        this.timelineVisible = false;
        this.commentsVisible = false;
        this.mediaVisible = false;
        setTimeout(() => {
            const involvedSection = document.getElementById("involved-section");
            involvedSection?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        });
    }
    toggleMedia() {
        this.mediaVisible = !this.mediaVisible;
        if (this.mediaVisible) {
            const ticketId = this.ticketId;
            this.router.navigate([`tickets/${ticketId}/fragment/media`]);
            this.timelineVisible = false;
            this.commentsVisible = false;
            this.involvedVisible = false;
            this.showMedia();
        }
    }
    showMedia() {
        this.mediaVisible = true;
        this.timelineVisible = false;
        this.commentsVisible = false;
        this.involvedVisible = false;
        setTimeout(() => {
            const mediaSection = document.getElementById("media-section");
            mediaSection?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
    }
    private editOrNot(reset: boolean = false) {
        var nav = this.nav.current;
        if (Navigation.NEW.toString() === nav.id) {
            this.editMode = true;
            this.ticketTitle = "ticketDetails.title.new.ticket";

            if (reset) {
                this.ticketForm.reset({
                    title: "",
                    description: "",
                    registrant: "",
                    registeredBy: 0,
                    account: "",
                    branchId: 0,
                    statusId: 0,
                    responsible: "",
                    assigned: 0,
                    departmentId: 0,
                    accountmanager: "",
                    priorityId: 0,
                    date_done: null,
                    durationEstimate: "",
                    archived: false,
                    hasRecurrence: false,
                    recurrence: null,
                });
            }
            this.ticketTitle = "ticketDetails.title.new.ticket";
        } else if ("edit" === nav.action) {
            this.ticketTitle = "ticketDetails.title.exist.ticket";
            this.editMode = true;
        } else if ("choose" === nav.action) {
            // preserve editMode while user is selecting a value in the dependent column
        } else {
            this.editMode = false;
        }
    }

    private initializeForm(): void {
        this.ticketForm = this.fb.group({
            title: "",
            description: "",
            registrant: "",
            registeredBy: 0,
            account: "",
            branchId: 0,
            statusId: 0,
            responsible: "",
            assigned: 0,
            departmentId: 0,
            accountmanager: "",
            priorityId: 0,
            date_done: null,
            durationEstimate: "",
            archived: false,
            hasRecurrence: false,
            recurrence: null,
        });
    }

    TicketEmpty(): boolean {
        const ticketValue = this.ticket.value;
        return (
            (!ticketValue || ticketValue.id === null || ticketValue.id === 0) &&
            !this.editMode
        );
    }

    private populateForm(ticket: Ticket): void {
        if (ticket.id) {
            this.ticket.next(ticket);
            this.populateFormWithCurrentTicket();
        } else {
            this.populateFormWithTicket(ticket);
        }
    }

    private populateFormWithCurrentTicket(): void {
        const ticket = this.ticket.value;
        this.populateFormWithTicket(ticket);
    }
    private populateFormWithTicket(
        ticket: Ticket,
        emit: boolean = false,
        form?: FormGroup<any>
    ): void {
        const processing = form || this.ticketForm;

        // Het terugkeerpatroon staat niet op het ticket zelf: het komt uit de
        // parts, of het is er een dat de gebruiker net heeft ingevuld en dat
        // nog nergens is opgeslagen. Draagt de meegegeven staat de velden, dan
        // wint die. Anders wist herpopuleren - bij het kiezen van een afdeling,
        // status of prioriteit - het patroon dat net is ingevuld.
        const carried = ticket as any;
        const carriesRecurrence =
            !!carried && Object.prototype.hasOwnProperty.call(carried, "hasRecurrence");

        // Een echt Ticket draagt de velden niet - het patroon zit in de parts -
        // dus daar valt niets uit over te nemen. Staat er dan al een patroon in
        // het formulier dat nog niet is opgeslagen, dan blijft dat staan: het
        // ticket opnieuw invullen mag niet wissen wat de gebruiker net koos.
        const inForm = (processing.value ?? {}) as any;
        const keepFromForm = this.editMode && !carriesRecurrence && !!inForm.hasRecurrence;

        processing.setValue(
            {
                title: ticket.title,
                description: ticket.description,
                registrant: ticket.registrant,
                registeredBy: ticket.registeredBy,
                account: ticket.account,
                branchId: ticket.branchId,
                statusId: ticket.statusId ?? null,
                responsible: ticket.responsible,
                assigned: ticket.assigned,
                departmentId: ticket.departmentId,
                accountmanager: ticket.accountmanager,
                priorityId: ticket.priorityId ?? null,
                date_done: ticket.date_done ?? null,
                durationEstimate: ticket.durationEstimate,
                archived: ticket.archived,
                hasRecurrence: carriesRecurrence
                    ? !!carried.hasRecurrence
                    : keepFromForm
                      ? true
                      : !!this.recurrence,
                recurrence: carriesRecurrence
                    ? carried.recurrence ?? null
                    : keepFromForm
                      ? inForm.recurrence ?? null
                      : this.recurrence
                        ? this.recurrence.pattern
                        : null,
            },
            { emitEvent: emit, onlySelf: false }
        );

        this.editOrNot();
        this.ticketTitle = this.editMode
            ? "ticketDetails.title.new.ticket"
            : "ticketDetails.title.exist.ticket";

        return processing.value;
    }

    toggleEditMode() {
        const ticketId = this.ticket.value.id;

        if (this.editMode) {
            this.router.navigate(["/tickets", ticketId]);
            this.editMode = false;
            this.nav.freshNavigation;
        } else {
            this.router.navigate(["/tickets", ticketId, "edit"]).then(() => {
                this.ticketService.selectFromRouter();

                this.editMode = true;
            });
        }
    }

    saveTicket(action: ActionButtonSubmit): void {
        this.isLoading = true;
        this.messages.next(Messages.EMPTY);
        if (this.ticketForm.valid) {
            const token = this.auth.token;
            const dateDone = this.ticketForm.value.date_done;

            if (dateDone && new Date(dateDone).getTime() < new Date().getTime()) {
                this.ticketForm.patchValue({
                    date_done: null,
                });
            }

            // The screen already keeps these fields shut, so this is the net
            // under it. What is sent is what was stored, not what the form
            // holds, so that the server - which checks the same thing - finds
            // them exactly as it has them.
            const locked = this.notYetActive ? this.lockedUntilActive : null;
            if (locked && this.changesWhatIsLocked(locked)) {
                this.isLoading = false;
                this.refuseWhatIsLocked();
                action.result(false);
                return;
            }

            const id = this.ticket.value.id;
            const updatedTicket = new Ticket(
                id === null || id < 1 ? Navigation.NEW : id,
                this.ticketForm.value.title || "",
                this.ticketForm.value.description || "",
                this.ticketForm.value.registrant || "",
                this.ticketForm.value.registeredBy || 0,
                this.ticketForm.value.account || "",
                this.ticketForm.value.branchId || 0,
                this.ticketForm.value.statusId || 0,
                this.ticketForm.value.responsible || "",
                this.ticketForm.value.assigned || 0,
                this.ticketForm.value.departmentId || 0,
                this.ticketForm.value.accountmanager || "",
                this.priorityToSave,
                this.ticketForm.value.date_done || null,
                this.ticketForm.value.durationEstimate || "",
                this.ticketForm.value.archived || false
            );
            if (locked) {
                updatedTicket.title = locked.title;
                updatedTicket.description = locked.description;
                updatedTicket.statusId = locked.statusId;
                updatedTicket.priorityId = locked.priorityId;
                updatedTicket.branchId = locked.branchId;
                updatedTicket.departmentId = locked.departmentId;
                updatedTicket.archived = locked.archived;
            }

            if (this.nav.currentId === "-9") {
                this.ticketService
                    .createTicket(token, updatedTicket)
                    .subscribe((messages) => {
                        if (messages.hasAnyMessages) {
                            this.isLoading = false;
                            this.messages.next(messages);
                            action.result(false);
                        } else {
                            const newTicketId = this.ticketService.currentTicket?.id;
                            if (newTicketId) {
                                this.saveRecurrence(token, newTicketId, () => {
                                    this.ticketService
                                        .getTicketWithParts(token, newTicketId)
                                        .subscribe((getMessages) => {
                                            this.receiveTicket(getMessages, action, token);
                                        });
                                });
                            }
                        }
                    });

                this.ticketTitle = "ticketDetails.title.exist.ticket";
            } else {
                this.ticketService
                    .updateTicket(token, updatedTicket)
                    .subscribe((messages) => {
                        if (messages.hasAnyMessages) {
                            this.isLoading = false;
                            this.messages.next(messages);
                            action.result(false);
                        } else {
                            const updatedTicketId =
                                this.ticketService.currentTicket?.id || updatedTicket.id;
                            if (updatedTicketId) {
                                this.saveRecurrence(token, updatedTicketId, () => {
                                    this.ticketService
                                        .getTicketWithParts(token, updatedTicketId)
                                        .subscribe((getMessages) => {
                                            this.receiveTicket(getMessages, action, token);
                                        });
                                });
                            }
                        }
                    });
            }
        }
    }

    // ------------------------------------------------------ terugkeerpatroon

    /**
     * Haalt het terugkeerpatroon uit de parts van het ticket, zodat het
     * detailscherm er geen eigen aanroep voor nodig heeft.
     */
    private readRecurrence(selectedTicket: Ticket): void {
        const raw =
            (this.ticketService.currentTicket as any)?.parts?.recurrence ??
            (selectedTicket as any)?.parts?.recurrence ??
            null;

        this.recurrence = RecurrenceView.from(raw);
        this.recurrenceService.useFromTicketParts(raw);
        this.rememberWhatIsLocked(selectedTicket, raw);
    }

    /**
     * Notes down whether this ticket is still ahead of its day, and what it
     * said when it was read.
     *
     * The generator plans the next ticket of a series only while too few are
     * open and the date is within reach. Closing a ticket meant for next month
     * today takes it out of that count while the dates after it are still out
     * of reach, so nothing new appears and the series looks broken (#164). Until
     * its day, the ticket keeps its title, description, status, priority,
     * location, department and place outside the archive.
     *
     * While editing, the snapshot of the same ticket is kept as it was: what
     * comes past then may already carry what is in the form.
     */
    private rememberWhatIsLocked(selectedTicket: Ticket, raw: any): void {
        const id = selectedTicket?.id ?? null;
        const withParts = [selectedTicket, this.ticketService.currentTicket].find(
            (one) => !!(one as any)?.parts && one?.id === id
        );
        const sameTicket = this.lockedUntilActive?.ticketId === id;

        if (!withParts) {
            if (!sameTicket) {
                this.lockedUntilActive = null;
            }
            return;
        }
        if (sameTicket && this.editMode) {
            return;
        }
        const scheduledOn: string | null = raw?.scheduledOn ?? null;

        this.lockedUntilActive =
            id && id > 0 && scheduledOn
                ? {
                    ticketId: id,
                    scheduledOn: scheduledOn,
                    title: withParts.title ?? "",
                    description: withParts.description ?? "",
                    statusId: withParts.statusId ?? 0,
                    priorityId: withParts.priorityId ?? 0,
                    branchId: withParts.branchId ?? 0,
                    departmentId: withParts.departmentId ?? 0,
                    archived: !!withParts.archived,
                }
                : null;
    }

    /** Whether the ticket on screen is planned for a day after today. */
    get notYetActive(): boolean {
        const locked = this.lockedUntilActive;
        const today = RecurrencePattern.asIsoDate(new Date()) ?? "";

        return (
            !!locked &&
            locked.ticketId === this.ticket.value?.id &&
            locked.scheduledOn > today
        );
    }

    /** The day the ticket becomes active, the way the screen writes dates. */
    get activeFromText(): string {
        const date = RecurrencePattern.asDate(this.lockedUntilActive?.scheduledOn);
        return date
            ? `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`
            : "";
    }

    /**
     * Whether the form asks to change what has to wait for the ticket's day.
     *
     * The description is compared as text: the editor may hand the same words
     * back wrapped differently, and that is not a change anyone made.
     */
    private changesWhatIsLocked(locked: LockedUntilActive): boolean {
        const form = this.ticketForm.value;
        const textOf = (html: string | null | undefined) =>
            (html ?? "").replace(/<[^>]*>/g, "").trim();

        return (
            (form.title ?? "") !== locked.title ||
            textOf(form.description) !== textOf(locked.description) ||
            (form.statusId || 0) !== (locked.statusId || 0) ||
            (form.priorityId || 0) !== (locked.priorityId || 0) ||
            (form.branchId || 0) !== (locked.branchId || 0) ||
            (form.departmentId || 0) !== (locked.departmentId || 0) ||
            !!form.archived !== locked.archived
        );
    }

    private refuseWhatIsLocked(): void {
        this.messages.next(Messages.from({ ticket: ["ticket.future.locked"] }));
    }

    get hasRecurrence(): boolean {
        return !!this.ticketForm?.value?.hasRecurrence;
    }

    /**
     * Switching 'recurrence' on or off.
     *
     * Switching off a series that has already produced tickets asks first what
     * should happen to those tickets; aborting leaves the switch on.
     */
    onRecurrenceToggle(event: Event): void {
        const checked = (event.target as HTMLInputElement).checked;

        if (checked) {
            this.ticketForm.patchValue({
                hasRecurrence: true,
                recurrence: this.ticketForm.value.recurrence ?? RecurrencePattern.EMPTY,
            });
            return;
        }
        // Whether this ticket already has a stored series is in the pattern
        // itself: a saved pattern has an id. Do not go by this.recurrence -
        // that field is empty while editing, and then the switch simply went
        // off without anything ever being asked.
        const stored = this.ticketForm.value.recurrence;
        const saved = !!stored && Number(stored.id) > 0;

        if (!saved) {
            // never saved: just take it away again
            this.ticketForm.patchValue({ hasRecurrence: false, recurrence: null });
            return;
        }
        this.confirmCancelRecurrence();
    }

    /**
     * Ending a series is a question for the panel beside the ticket. The panel
     * fetches the impact itself from the ticket in the url, so nothing here has
     * to survive the trip.
     */
    private confirmCancelRecurrence(): void {
        const ticketId = this.ticket.value.id || Number(this.nav.currentId);

        if (!ticketId || ticketId < 1) {
            return;
        }
        this.askInPanel(ticketId, "recurrence-cancel");
    }

    private cancelRecurrence(
        token: string,
        ticketId: number,
        future: RecurrenceFuture
    ): void {
        this.isLoading = true;
        this.recurrenceService
            .cancel(token, ticketId, future)
            .subscribe((messages) => {
                this.isLoading = false;
                if (messages.hasAnyMessages) {
                    this.messages.next(messages);
                    this.restoreRecurrenceCheckbox();
                    return;
                }
                this.recurrence = null;
                this.ticketForm.patchValue({
                    hasRecurrence: false,
                    recurrence: null,
                });
                this.updateTicketList(token);
            });
    }

    /** Zet het vinkje terug aan wanneer het annuleren niet doorgaat. */
    private restoreRecurrenceCheckbox(): void {
        this.ticketForm.patchValue({
            hasRecurrence: true,
            recurrence: this.recurrence ? this.recurrence.pattern : null,
        });
    }

    /**
     * Slaat het patroon op nadat het ticket zelf is bewaard.
     *
     * Een nieuw patroon laat de backend meteen de eerstvolgende tickets
     * aanmaken, dus daarna wordt de lijst ververst.
     */
    private saveRecurrence(
        token: string,
        ticketId: number,
        whenDone: () => void
    ): void {
        const wanted = this.ticketForm.value.hasRecurrence;
        const pattern = this.ticketForm.value.recurrence;

        if (!wanted || !pattern) {
            whenDone();
            return;
        }
        const rule = RecurrencePattern.from(pattern);

        // The answer may already be in: then we are back from the panel and
        // there is nothing left to ask. It is held in a field rather than read
        // from the url again, because the url was cleaned the moment it arrived.
        const answered = this.pendingChange;
        this.pendingChange = null;
        if (answered) {
            this.storeRecurrence(token, ticketId, rule, answered, whenDone);
            return;
        }
        // Do not guess here whether this ticket has a series: this.recurrence
        // is refilled on every ticket switch and is not reliable while editing.
        // The server knows, and answers 404 when there is no series - and then
        // there is nothing to ask either.
        this.askAboutTheFuture(token, ticketId, rule, whenDone);
    }

    /**
     * Asks first what should happen to the tickets already planned, but only
     * when there is something to lose. If none of them has been worked on, the
     * new rule simply moves them without troubling the user.
     */
    private askAboutTheFuture(
        token: string,
        ticketId: number,
        rule: RecurrencePattern,
        whenDone: () => void
    ): void {
        this.recurrenceService
            .impactOfChange(token, ticketId, rule)
            .subscribe((messages) => {
                if (messages.hasAnyMessages) {
                    this.isLoading = false;
                    this.messages.next(messages);
                    return;
                }
                const impact = this.recurrenceService.currentImpact.value;

                // no series (404), or nothing to lose: just save
                if (!impact || !impact.atRisk) {
                    this.storeRecurrence(token, ticketId, rule, "AFTER_WORKED", whenDone);
                    return;
                }
                // There is something to lose, so we ask - in the panel that
                // slides in beside the ticket, not in a window on top of it.
                // The ticket itself has not been saved yet: the question comes
                // before the save, so that no half stored ticket is left behind
                // if the user navigates away on a phone.
                this.isLoading = false;
                this.askInPanel(ticketId, "recurrence-change");
            });
    }


    /**
     * Reads an answer a panel handed back through the url.
     *
     * A query parameter rather than a callback, because on a phone this screen
     * is destroyed while the panel is open: there is nothing left to call back
     * into, but the url survives.
     */
    private answerFromUrl(name: string): string | null {
        const query = this.router.url.split("?")[1];
        if (!query) {
            return null;
        }
        const answer = new URLSearchParams(query).get(name);
        return answer && answer.length > 0 ? answer : null;
    }

    /**
     * Sends the user to the panel that asks the question, having first put the
     * form somewhere that survives the trip.
     */
    private askInPanel(ticketId: number, topic: string): void {
        this.ticketService.setFormState(this.ticketForm.value);

        const choice = new SingleChoice(topic);
        choice.setup();

        // After navigating, the frame reads the choice from the navigation
        // service rather than from the emit: that one is gone the moment this
        // screen disappears. startChoice puts it somewhere that survives the
        // transition.
        this.nav.startChoice(choice);
        this.choiceEvent.emit(choice);

        this.router.navigate([`tickets/${ticketId}/choose/${topic}`]);
    }

    private storeRecurrence(
        token: string,
        ticketId: number,
        rule: RecurrencePattern,
        choice: RecurrenceChange,
        whenDone: () => void
    ): void {
        this.recurrenceService
            .save(token, ticketId, rule, choice)
            .subscribe((messages) => {
                if (messages.hasAnyMessages) {
                    this.isLoading = false;
                    this.messages.next(messages);
                    return;
                }
                whenDone();
            });
    }

    receiveTicket(messages: Messages, action: ActionButtonSubmit, token: string) {
        this.isLoading = false;
        this.messages.next(messages);

        if (messages.hasAnyMessages) {
            action.result(false);
        } else {
            action.result(true);
            this.ticketService.clearFormStateBackup();
            this.updateTicketList(token);

            const newTicketId = this.ticketService.currentTicket?.id;

            if (newTicketId && newTicketId > 0) {
                this.nav.clearNavigationState();

                // Een vers ticket begint altijd op het eerste tabblad, hoe het
                // scherm ook stond toen het werd aangemaakt: opslaan van een
                // nieuw ticket eindigt zo altijd hetzelfde.
                let currentTab = "comment";

                if (this.isCreating) {
                    this.selectTab(0);
                } else {
                    switch (this.selectedIndex) {
                        case 0:
                            currentTab = "comment";
                            break;
                        case 1:
                            currentTab = "media";
                            break;
                        case 2:
                            currentTab = "involved";
                            break;
                        case 3:
                            currentTab = "timeline";
                            break;
                    }
                }

                this.router
                    .navigate([`/tickets/${newTicketId}/fragmentedit/${currentTab}/-9`])
                    .then(() => {
                        this.editMode = false;
                        this.isCreating = false;
                    });

            }
        }
    }

    private updateTicket(ticket: Ticket): Ticket {
        const id = this.ticket.value.id;
        ticket.id = id;
        ticket.title = this.ticketForm.value.title || "";
        ticket.description = this.ticketForm.value.description || "";
        ticket.registrant = this.ticketForm.value.registrant || "";
        ticket.registeredBy = this.ticketForm.value.registeredBy || 0;
        ticket.account = this.ticketForm.value.account || "";
        ticket.branchId = this.ticketForm.value.branchId || 0;
        ticket.statusId = this.ticketForm.value.statusId || 0;
        ticket.responsible = this.ticketForm.value.responsible || "";
        ticket.assigned = this.ticketForm.value.assigned || 0;

        ticket.departmentId = this.ticketForm.value.departmentId || 0;
        ticket.accountmanager = this.ticketForm.value.accountmanager || "";
        ticket.priorityId = this.ticketForm.value.priorityId || 0;
        ticket.date_done = this.ticketForm.value.date_done || null;
        ticket.durationEstimate = this.ticketForm.value.durationEstimate || "";
        ticket.archived = this.ticketForm.value.archived || false;
        return ticket;
    }

    private get updatedExistingTicket(): Ticket {
        const existingTicket = this.ticket.value;
        return this.updateTicket(existingTicket);
    }

    /**
     * Archives the ticket instead of deleting it.
     *
     * The button used to send DELETE by-id, which takes the ticket and
     * everything hanging off it away for good. What is wanted is that it leaves
     * the list and stays findable, and that is the archive flag: the active
     * filter hides it, the archived filter finds it, and nothing is thrown
     * away. It is the same thing the bulk 'archiveren' action does to a whole
     * selection.
     *
     * The old version navigated away whatever the server answered, including
     * when it answered with an error, so a refusal was reported to a screen
     * that was already gone. It now stays put and shows what came back.
     */
    archiveTicket(): void {
        this.setArchived(true);
    }

    activateTicket(): void {
        this.setArchived(false);
    }

    /**
     * Puts the ticket in the archive, or takes it back out.
     *
     * Archiving is what the button in the header does now. It used to send
     * DELETE by-id, which takes the ticket and everything hanging off it away
     * for good; what is wanted is that it leaves the list and stays findable,
     * and that is this flag. The active filter hides it, the archived filter
     * finds it, and nothing is thrown away. The bulk action does the same to a
     * whole selection.
     *
     * Archiving goes back to the list, because the ticket is no longer in it.
     * Un-archiving stays where it is: it was found through the archive, and
     * sending the user to a list that no longer holds it would be a strange way
     * to confirm that it is back.
     *
     * On a refusal the screen stays put and shows what came back. The version
     * before this navigated away whatever the server said, and the error
     * landed on a screen that was already leaving.
     */
    /**
     * A copy of the ticket carrying nothing but its own fields.
     *
     * The loaded ticket has parts hanging off it - comments, media, the branch,
     * the assignee - because it arrives from with-parts. Sending that back is
     * more than the server will take: it answers 400 before the resource method
     * runs, which is why nothing appears in its log. Saving from the edit
     * screen has always built a fresh Ticket from the form for the same reason;
     * this builds one from the ticket instead, because the archive button is
     * there in view mode, where the form is not the thing to trust.
     */
    private withArchived(ticket: Ticket, archived: boolean): Ticket {
        return new Ticket(
            ticket.id,
            ticket.title,
            ticket.description,
            ticket.registrant,
            ticket.registeredBy,
            ticket.account,
            ticket.branchId,
            ticket.statusId ?? 0,
            ticket.responsible,
            ticket.assigned,
            ticket.departmentId ?? 0,
            ticket.accountmanager,
            ticket.priorityId ?? 0,
            ticket.date_done,
            ticket.durationEstimate,
            archived
        );
    }

    /**
     * Leaves the details side empty, with nothing selected.
     *
     * An archived ticket has been put away, and going on showing it - or
     * jumping to whichever ticket is next in the list - invites the user to do
     * the same thing again to something they did not mean to touch. Empty is
     * the honest answer: it says the work is done and asks what is next.
     *
     * The list marks its own row by object identity, and reloading it hands
     * back new objects, so the highlight goes on its own.
     */
    private clearSelection(): void {
        this.ticketService.selectedTicket.next(Ticket.EMPTY);
        this.ticket.next(Ticket.EMPTY);
        this.editMode = false;
    }

    private setArchived(archived: boolean): void {
        if (this.notYetActive) {
            this.refuseWhatIsLocked();
            return;
        }
        const token = this.auth.token;
        const ticket = this.ticket.value;

        if (!ticket || !ticket.id) {
            return;
        }
        const before = ticket.archived;

        this.ticketService
            .updateTicket(token, this.withArchived(ticket, archived))
            .subscribe((messages) => {
                this.messages.next(messages);

                if (messages.hasAnyMessages) {
                    ticket.archived = before;
                    return;
                }
                ticket.archived = archived;
                this.updateTicketList(token);

                if (archived) {
                    this.clearSelection();
                    this.router.navigate(["tickets"]);
                }
            });
    }

    updateTicketList(token: string): void {
        this.ticketService.search(token).subscribe((messages) => {
            const updatedTicket = this.ticketService.ticketData;
            this.ticketService._currentTicket.next(updatedTicket);
        });
    }
    private updateTimelineView(ticket: Ticket): void {
        const view = (ticket.timeline || []).map((tl) => ({
            id: tl.id,
            ticket: tl.ticket,
            topic: tl.topic,
            reference: tl.reference,
            oldReference: tl.oldReference,
            action: tl.action,
            actor: tl.actor,
            changed: tl.changed,
            title: tl.title ?? `${tl.action} - ${tl.topic}`,
            timestamp: tl.changed,
            description: `Referência: ${tl.reference}, Tópico: ${tl.topic}`,
            type: tl.topic,
            user: tl.actor
                ? { id: tl.actor, name: `Usuário #${tl.actor}` }
                : undefined,
        }));
        this.timelineView.next(view);
        this.showTimeline();
    }

    /**
     * A pick list closed without a pick - by its close button, another tab,
     * or back - ends the choice as well. Only a pick used to, and until then
     * this component ignored the next ticket chosen from the list (#179).
     *
     * Waits a turn: after a pick the list first navigates back here and only
     * then hands over the value, which still needs the flag.
     */
    private endChoiceLeftUnanswered(): void {
        if (this.disableEditingDuringChoice && !this.router.url.includes("/choose/")) {
            setTimeout(() => {
                if (!this.router.url.includes("/choose/")) {
                    this.disableEditingDuringChoice = false;
                }
            });
        }
    }

    relaySingleChoice(choice: SingleChoice) {
        this.disableEditingDuringChoice = true;
        this.ticketService.setFormState(this.ticketForm.value);
        this.nav.startChoice(choice);
    }

    get branchIdObserver(): Partial<Observer<number>> {
        return {
            next: (id) => {
                this.onBranchChoice(id);
            },
        };
    }

    onBranchChoice(branchId: number): void {
        const current = this.populateAndSynchonize(this.ticket.value);
        current.branchId = branchId;
        this.ticketForm.patchValue({
            branchId: branchId,
        });
    }
    get statusIdObserver(): Partial<Observer<number>> {
        return {
            next: (id) => {
                this.onStatusChoice(id);
            },
        };
    }
    onStatusChoice(statusId: number): void {
        const current = this.populateAndSynchonize(this.ticket.value);
        current.statusId = statusId;

        this.ticketForm.patchValue({
            statusId: statusId,
        });
    }
    get priorityIdObserver(): Partial<Observer<number>> {
        return {
            next: (id) => {
                this.onPriorityChoice(id);
            },
        };
    }
    onPriorityChoice(priorityId: number): void {
        const current = this.populateAndSynchonize(this.ticket.value);
        current.priorityId = priorityId;
        this.ticketForm.patchValue({
            priorityId: priorityId,
        });
    }
    get departmentIdObserver(): Partial<Observer<number>> {
        return {
            next: (id) => {
                this.onDepartmentChoice(id);
            },
        };
    }

    onDepartmentChoice(departmentId: number): void {
        this.messages.next(Messages.EMPTY);
        const current = this.populateAndSynchonize(this.ticket.value);
        current.departmentId = departmentId;
        this.ticketForm.patchValue({
            departmentId: departmentId,
        });

        this.ticket.value, this.ticketForm.value;
    }

    private populateAndSynchonize(current: Ticket): Ticket {
        this.populateFormWithCurrentTicket();
        const state = this.ticketService.restoreFormState(false);
        this.populateFormWithTicket(state as Ticket, true, this.ticketForm);
        this.editMode = true;
        const updated = this.updateTicket(current);
        this.disableEditingDuringChoice = false;

        return updated;
    }
    get assignedIdObserver(): Partial<Observer<number>> {
        return {
            next: (id) => {
                this.onAssignedChoice(id);
            },
        };
    }
    onAssignedChoice(assignedId: number): void {
        const current = this.populateAndSynchonize(this.ticket.value);
        current.assigned = assignedId;

        this.ticketForm.patchValue({
            assigned: assignedId,
        });
    }

    /**
     * The two priorities the switch offers, named by importance instead of by
     * id. The table holds more, but the customer asked for these two on the
     * form; the others stay in the database for the tickets that already have
     * them.
     *
     * Ids used to be written here as 1 and 3, and that is what #170 is about:
     * the databases were filled separately, so id 3 is 'normaal' on the test
     * one and something else on acceptance. The form stored 3 and said
     * 'Normaal' beside it, and the ticket then showed the word that id really
     * carries there - 'laag' in the report. Importance is the ranking the
     * backend gives every priority and it means the same in every database: 5
     * for the high one, 3 for the middle one, 1 for the low one, 2 for the rest
     * (Issue38AddImportanceToPriorities). Neither the id nor the label the
     * customer may rename decides any more.
     */
    private static readonly IMPORTANCE_HIGH = 5;
    private static readonly IMPORTANCE_NORMAL = 3;

    /**
     * The priority whose importance sits closest to the one asked for.
     *
     * Nearest, not equal: a database whose middle priority is called something
     * the backend did not recognise never got the 3 and would leave an exact
     * match with nothing to store. 'Midden' and 'middel' are repaired there by
     * Issue170GiveTheMiddlePriorityItsImportance, but this side does not have to
     * wait for that to have run, and it still has an answer for a word nobody
     * has thought of: the nearest to 3 is the middle one at 2, not 'laag' at 1.
     *
     * Null while the list is still on its way, which is why every caller has
     * an answer for that case.
     */
    private priorityNearest(importance: number): Priority | null {
        let nearest: Priority | null = null;
        let distance = Number.MAX_VALUE;

        for (const candidate of this.priority.prioritiesData ?? []) {
            const candidateDistance = Math.abs(candidate.importance - importance);
            if (candidateDistance < distance) {
                nearest = candidate;
                distance = candidateDistance;
            }
        }
        return nearest;
    }

    /** The priority the switch stores when it is on. */
    get highPriority(): Priority | null {
        return this.priorityNearest(TicketDetailsComponent.IMPORTANCE_HIGH);
    }

    /** The priority the switch stores when it is off. */
    get normalPriority(): Priority | null {
        return this.priorityNearest(TicketDetailsComponent.IMPORTANCE_NORMAL);
    }

    /** The one of the two the switch is showing right now. */
    private get priorityShown(): Priority | null {
        return this.priorityIsHigh ? this.highPriority : this.normalPriority;
    }

    /**
     * The priority a save should store.
     *
     * A ticket is normal unless someone set it high - the customer's rule - so
     * the screen no longer shows 'Normaal' over a ticket that has no priority
     * at all, or over one still sitting on a priority the form does not offer.
     * Saving writes what the screen says.
     *
     * Without the list there is nothing to resolve, so an existing ticket keeps
     * the priority it has and a new one is left unset. Both are visible on the
     * screen and can be corrected; a guessed id would be neither.
     */
    get priorityToSave(): number {
        const shown = this.priorityShown;
        return shown?.id ?? this.ticketForm?.value?.priorityId ?? 0;
    }

    /** Whether the switch is on. Everything that is not the high one reads as normal. */
    get priorityIsHigh(): boolean {
        const high = this.highPriority;
        return !!high && this.ticketForm?.value?.priorityId === high.id;
    }

    /**
     * The word on the switch: the priority's own, out of the same row the save
     * will store. The template used to spell 'Hoog' and 'Normaal' itself, so
     * the form could name one priority and write another without anything
     * catching it.
     */
    get prioritySwitchLabel(): string {
        return this.priorityShown?.value ?? "--";
    }

    /**
     * The colour the customer gave the priority the switch is showing.
     *
     * It asks for the priority the *label* names, not the one the ticket
     * happens to hold. Those differ: the label reads as normal for anything
     * that is not the high one, including a ticket with no priority at all and
     * the older ones sitting on priorities that are no longer offered. Looking
     * up the stored id gave those a colour that did not belong to the word
     * beside it, and gave an unset one a grey so pale that the pill looked like
     * it had failed to draw.
     */
    get priorityColour(): string {
        return this.priorityShown?.color || "#cccccc";
    }

    get ticketId(): number {
        const ticket = this.ticket.value;
        return ticket && ticket.id ? ticket.id : Navigation.NEW;
    }
    get branchId(): number {
        const ticket = this.ticket.value;

        return ticket && ticket.branchId ? ticket.branchId : -1;
    }

    get branchLabel(): string {
        const id = this.branchId;

        let label = "-";
        if (id > 1) {
            const partId = this.ticket.value?.parts?.branch?.id;
            if (id === partId) {
                label = BranchService.shortName(this.ticket.value.parts.branch);
            } else {
                label = this.branches.labelForId(id);
            }
        }
        return label;
    }
    get branchLabelerByService(): Labeler {
        return this.branches;
    }

    get statusId(): number {
        const ticket = this.ticket.value;
        return ticket && ticket.statusId ? ticket.statusId : -1;
    }

    get statusLabel(): string {
        const id = this.statusId;
        let label = "--";
        if (id > 0) {
            const partId = this.ticket.value?.parts?.status?.id;
            if (id === partId) {
                label = this.ticket.value.parts.status.name;
            } else {
                label = this.status.labelForId(id);
            }
        }
        return label;
    }

    get statusLabelerByService(): Labeler {
        return this.status;
    }

    get priorityId(): number {
        const ticket = this.ticket.value;
        return ticket && ticket.priorityId ? ticket.priorityId : -1;
    }

    get priorityLabel(): string {
        const id = this.priorityId;

        let label = "--";
        if (id > 0) {
            const partId = this.ticket.value?.parts?.priority?.id;
            if (id === partId) {
                label = this.ticket.value.parts.priority.value;
            } else {
                label = this.priority.labelForId(id);
            }
        }
        return label;
    }
    get priorityLabelerByService(): Labeler {
        return this.priority;
    }

    get departmentId(): number {
        const ticket = this.ticket.value;
        return ticket && ticket.departmentId ? ticket.departmentId : -1;
    }

    get departmentLabel(): string {
        const id = this.departmentId;

        let label = "--";
        if (id > 1) {
            const partId = this.ticket.value?.parts?.department?.id;
            if (id === partId) {
                label = this.ticket.value.parts.department.name;
            } else {
                label = this.department.labelForId(id);
            }
        }
        return label;
    }
    get departmentLabelerByService(): Labeler {
        return this.department;
    }

    get assignedId(): number {
        const ticket = this.ticket.value;
        return ticket && ticket.assigned ? ticket.assigned : -1;
    }

    get assignedLabel(): string {
        const id = this.assignedId;

        let label = "-";
        if (id > 1) {
            const partId = this.ticket.value?.parts?.assigned?.id;
            if (id === partId) {
                label = this.ticket.value.parts.assigned.name;
            } else {
                label = this.assigned.labelForId(id);
            }
        }
        return label;
    }
    get assignedLabelerByService(): Labeler {
        return this.assigned;
    }
    get TRUE(): boolean {
        return true;
    }
    get FALSE(): boolean {
        return false;
    }

    get isCreatingMode(): boolean {
        return !this.editMode;
    }

    onPriorityToggle(event: Event): void {
        const checked = (event.target as HTMLInputElement).checked;

        if (this.notYetActive) {
            (event.target as HTMLInputElement).checked = this.priorityIsHigh;
            return;
        }
        const chosen = checked ? this.highPriority : this.normalPriority;
        const priorityId = chosen?.id ?? this.ticketForm.value.priorityId ?? 0;

        this.ticketForm.patchValue({
            priorityId: priorityId,
        });

        this.onPriorityChoice(priorityId);
    }

    cancelTicket(event?: MouseEvent): void {
        event?.preventDefault();
        event?.stopPropagation();

        this.router.navigateByUrl('/tickets/-1/result/page/1').then(() => {
            window.location.reload();
        });
    }
}
